import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const CHART_GOLD = '#C9A84C';
const CHART_GOLD_LIGHT = '#f5e6b8';
const CHART_GRID = 'rgba(201, 168, 76, 0.15)';
const CHART_AXIS = '#8896a8';
const CHART_HEIGHT = 280;

function formatRevenue(value) {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  return `₹${Number(value || 0).toLocaleString('en-IN')}`;
}

function fmtInr(amount) {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function useChartWidth() {
  const [width, setWidth] = useState(320);
  const ref = React.useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect?.width || 0);
      if (next > 0) setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, width: Math.max(width, 280) };
}

function FleetTooltip({ active, payload, label, bookingsLabel }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      className="rounded-xl border border-de-gold/40 px-4 py-3 shadow-2xl"
      style={{ background: 'linear-gradient(145deg, #0f2040, #0D1B2A)' }}
    >
      <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-de-gold/80">{label}</p>
      <p className="m-0 mt-1 text-lg font-bold text-de-gold">{formatRevenue(row.revenue)}</p>
      <p className="m-0 mt-0.5 text-[10px] text-de-muted">
        {bookingsLabel}: {row.bookings ?? 0}
      </p>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex h-[280px] flex-col justify-center gap-3 px-4">
      <div className="de-skeleton h-3 w-1/3" />
      <div className="de-skeleton h-[200px] w-full rounded-xl" />
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }) {
  return (
    <div className="de-ultra-card">
      <div className="border-b border-de-gold/15 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="de-stat-ring h-10 w-10">
            <Icon className="h-5 w-5 text-de-gold" />
          </div>
          <div>
            <p className="m-0 text-[10px] font-bold uppercase tracking-[0.2em] text-de-gold/70">Analytics</p>
            <h3 className="m-0 text-sm font-bold text-white">{title}</h3>
          </div>
        </div>
      </div>
      <div className="p-4 pt-2">{children}</div>
    </div>
  );
}

function statusBadgeClass(status) {
  if (status === 'Active') {
    return 'bg-emerald-500/15 border-emerald-500 text-emerald-400';
  }
  return 'bg-amber-500/15 border-amber-500 text-amber-400';
}

export default function AdminFleetAnalytics({ authToken }) {
  const { t } = useLanguage();
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { ref: barWrapRef, width: barWidth } = useChartWidth();

  const loadAnalytics = useCallback(async () => {
    if (!authToken) {
      setError(t('sessionExpiredRefresh'));
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/analytics/fleet?months=${months}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      setData(json);
    } catch (err) {
      setData(null);
      setError(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, months, t]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const trendSeries = useMemo(
    () => (Array.isArray(data?.monthly_trend) ? data.monthly_trend : []),
    [data],
  );

  const maxRevenue = useMemo(
    () => Math.max(...trendSeries.map((s) => Number(s.revenue || 0)), 1),
    [trendSeries],
  );

  const displayMachines = useMemo(() => {
    const rows = data?.machines;
    if (!Array.isArray(rows)) return [];
    return [...rows]
      .filter((row) => Number(row.total_bookings || 0) > 0 || Number(row.total_revenue || 0) > 0)
      .sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0));
  }, [data]);

  const chartMargin = useMemo(() => ({ top: 12, right: 12, left: 4, bottom: 4 }), []);
  const emptyTrend = !loading && !error && trendSeries.length === 0;

  const summaryCards = [
    { icon: '💰', val: fmtInr(data?.summary?.total_revenue), label: t('adminFleetPlatformRevenue') },
    { icon: '📋', val: `${data?.summary?.total_bookings ?? 0}`, label: t('analyticsBookings') },
    { icon: '⏱️', val: `${data?.summary?.total_hours ?? 0}`, label: t('analyticsHours') },
    { icon: '🚜', val: `${data?.summary?.active_machines ?? 0}`, label: t('analyticsActiveMachines') },
    { icon: '👷', val: `${data?.summary?.total_owners ?? 0}`, label: t('adminFleetActiveOwners') },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-lg font-bold text-de-gold">{t('adminFleetTitle')}</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 3, labelKey: 'analytics3Months' },
            { value: 6, labelKey: 'analytics6Months' },
            { value: 12, labelKey: 'analytics12Months' },
          ].map(({ value, labelKey }) => {
            const active = months === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setMonths(value)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                  active
                    ? 'border-de-gold bg-de-gold/20 text-de-gold'
                    : 'border-de-gold/35 bg-black/20 text-de-muted hover:border-de-gold/60',
                )}
              >
                {t(labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {loading ? (
          <div className="de-ultra-card col-span-full p-5">
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          </div>
        ) : error ? (
          <div className="de-ultra-card col-span-full p-5">
            <p className="m-0 mb-3 text-sm text-de-accent">{error}</p>
            <button
              type="button"
              onClick={loadAnalytics}
              className="rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold"
            >
              {t('retryLabel')}
            </button>
          </div>
        ) : (
          summaryCards.map((card) => (
            <div key={card.label} className="de-ultra-card p-5 text-center">
              <p className="m-0 text-2xl">{card.icon}</p>
              <p className="m-0 mt-2 text-xl font-bold text-white">{card.val}</p>
              <p className="m-0 mt-1 text-[11px] uppercase tracking-wide text-de-muted">{card.label}</p>
            </div>
          ))
        )}
      </div>

      <ChartCard title={t('adminFleetMonthlyTrend')} icon={BarChart3}>
        {loading ? (
          <ChartSkeleton />
        ) : error ? (
          <p className="py-20 text-center text-sm text-de-accent">{error}</p>
        ) : emptyTrend ? (
          <p className="py-20 text-center text-sm text-de-muted">{t('adminFleetNoData')}</p>
        ) : (
          <div ref={barWrapRef} className="w-full min-w-0" style={{ height: CHART_HEIGHT }}>
            <BarChart width={barWidth} height={CHART_HEIGHT} data={trendSeries} margin={chartMargin} barCategoryGap="20%">
              <defs>
                <linearGradient id="adminFleetBarGoldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_GOLD_LIGHT} />
                  <stop offset="100%" stopColor={CHART_GOLD} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={{ stroke: CHART_GRID }} tickLine={false} />
              <YAxis
                tick={{ fill: CHART_AXIS, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatRevenue}
                domain={[0, maxRevenue * 1.1]}
              />
              <Tooltip content={<FleetTooltip bookingsLabel={t('analyticsBookings')} />} />
              <Bar dataKey="revenue" fill="url(#adminFleetBarGoldGrad)" radius={[8, 8, 0, 0]} maxBarSize={56} isAnimationActive={false} />
            </BarChart>
          </div>
        )}
      </ChartCard>

      <div className="de-ultra-card overflow-hidden">
        <div className="border-b border-de-gold/15 px-5 py-4">
          <h3 className="m-0 text-sm font-bold text-white">{t('adminFleetMachineTable')}</h3>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          ) : error ? (
            <p className="m-0 text-sm text-de-accent">{error}</p>
          ) : displayMachines.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('adminFleetNoData')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse text-xs">
                <thead>
                  <tr className="border-b border-white/10">
                    {[t('machine'), t('adminFleetOwnerCol'), t('revenue'), t('analyticsBookings'), t('analyticsHours'), t('analyticsLastBooking'), t('status')].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-de-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayMachines.map((row) => {
                    const lastBookingLabel = row.last_booking_date
                      ? new Date(row.last_booking_date).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })
                      : '—';
                    const machineLabel = row.machine_name
                      ? `${row.machine_id} · ${row.machine_name}`
                      : row.machine_id;
                    return (
                      <tr key={row.machine_uuid || row.machine_id} className="border-b border-white/5">
                        <td className="px-2 py-2 font-bold text-de-gold">{machineLabel}</td>
                        <td className="px-2 py-2 text-de-text">{row.ownerName || '—'}</td>
                        <td className="px-2 py-2 font-bold text-de-gold">{fmtInr(row.total_revenue)}</td>
                        <td className="px-2 py-2 text-de-text">{row.total_bookings ?? 0}</td>
                        <td className="px-2 py-2 text-de-text">{row.total_hours ?? 0}</td>
                        <td className="px-2 py-2 text-de-text">{lastBookingLabel}</td>
                        <td className="px-2 py-2">
                          <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass(row.status)}`}>
                            {row.status || '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
